import { Renderer } from './Renderer'

export interface MouseState {
  x: number
  y: number
  worldX: number
  worldY: number
  tileX: number
  tileY: number
  leftDown: boolean
  rightDown: boolean
  middleDown: boolean
}

export interface DragState {
  active: boolean
  startX: number
  startY: number
  currentX: number
  currentY: number
}

export interface SelectionBox {
  active: boolean
  x1: number
  y1: number
  x2: number
  y2: number
}

export type InputEventType =
  | 'leftClick'
  | 'rightClick'
  | 'leftDragStart'
  | 'leftDragEnd'
  | 'rightDragStart'
  | 'rightDragEnd'
  | 'scroll'
  | 'pan'
  | 'keyDown'
  | 'keyUp'

export interface InputEvent {
  type: InputEventType
  x?: number
  y?: number
  worldX?: number
  worldY?: number
  key?: string
  delta?: number
  deltaX?: number
  deltaY?: number
  shiftKey?: boolean
  ctrlKey?: boolean
}

type InputCallback = (event: InputEvent) => void

export class Input {
  private renderer: Renderer
  private canvas: HTMLCanvasElement
  private callbacks: Map<InputEventType, InputCallback[]> = new Map()

  mouse: MouseState = {
    x: 0,
    y: 0,
    worldX: 0,
    worldY: 0,
    tileX: 0,
    tileY: 0,
    leftDown: false,
    rightDown: false,
    middleDown: false
  }

  drag: DragState = {
    active: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0
  }

  selection: SelectionBox = {
    active: false,
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 0
  }

  keys: Set<string> = new Set()

  private edgeScrollSpeed: number = 500
  private edgeScrollMargin: number = 30

  constructor(renderer: Renderer) {
    this.renderer = renderer
    this.canvas = renderer.app.canvas as HTMLCanvasElement
    this.setupEventListeners()
  }

  private setupEventListeners(): void {
    // Mouse events
    this.canvas.addEventListener('mousedown', this.onMouseDown.bind(this))
    this.canvas.addEventListener('mouseup', this.onMouseUp.bind(this))
    this.canvas.addEventListener('mousemove', this.onMouseMove.bind(this))
    this.canvas.addEventListener('wheel', this.onWheel.bind(this))
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault())

    // Keyboard events
    document.addEventListener('keydown', this.onKeyDown.bind(this))
    document.addEventListener('keyup', this.onKeyUp.bind(this))

    // Prevent default drag behavior
    this.canvas.addEventListener('dragstart', (e) => e.preventDefault())
  }

  private updateMouseWorld(): void {
    const world = this.renderer.screenToWorld(this.mouse.x, this.mouse.y)
    this.mouse.worldX = world.x
    this.mouse.worldY = world.y
    const tile = this.renderer.worldToTile(world.x, world.y)
    this.mouse.tileX = tile.x
    this.mouse.tileY = tile.y
  }

  private onMouseDown(e: MouseEvent): void {
    this.mouse.x = e.clientX
    this.mouse.y = e.clientY
    this.updateMouseWorld()

    if (e.button === 0) {
      this.mouse.leftDown = true
      this.drag.active = true
      this.drag.startX = this.mouse.worldX
      this.drag.startY = this.mouse.worldY
      this.drag.currentX = this.mouse.worldX
      this.drag.currentY = this.mouse.worldY
      this.selection.active = true
      this.selection.x1 = this.mouse.worldX
      this.selection.y1 = this.mouse.worldY
      this.selection.x2 = this.mouse.worldX
      this.selection.y2 = this.mouse.worldY
      this.emit('leftDragStart', {
        type: 'leftDragStart',
        x: this.mouse.x,
        y: this.mouse.y,
        worldX: this.mouse.worldX,
        worldY: this.mouse.worldY,
        shiftKey: e.shiftKey,
        ctrlKey: e.ctrlKey
      })
    } else if (e.button === 2) {
      this.mouse.rightDown = true
      this.emit('rightDragStart', {
        type: 'rightDragStart',
        x: this.mouse.x,
        y: this.mouse.y,
        worldX: this.mouse.worldX,
        worldY: this.mouse.worldY,
        shiftKey: e.shiftKey,
        ctrlKey: e.ctrlKey
      })
    } else if (e.button === 1) {
      this.mouse.middleDown = true
    }
  }

  private onMouseUp(e: MouseEvent): void {
    this.mouse.x = e.clientX
    this.mouse.y = e.clientY
    this.updateMouseWorld()

    if (e.button === 0) {
      this.mouse.leftDown = false

      // Check if it was a click (small drag distance) or a drag
      const dragDist = Math.sqrt(
        Math.pow(this.mouse.worldX - this.drag.startX, 2) +
        Math.pow(this.mouse.worldY - this.drag.startY, 2)
      )

      if (dragDist < 5) {
        this.emit('leftClick', {
          type: 'leftClick',
          x: this.mouse.x,
          y: this.mouse.y,
          worldX: this.mouse.worldX,
          worldY: this.mouse.worldY,
          shiftKey: e.shiftKey,
          ctrlKey: e.ctrlKey
        })
      }

      this.emit('leftDragEnd', {
        type: 'leftDragEnd',
        x: this.mouse.x,
        y: this.mouse.y,
        worldX: this.mouse.worldX,
        worldY: this.mouse.worldY,
        shiftKey: e.shiftKey,
        ctrlKey: e.ctrlKey
      })

      this.drag.active = false
      this.selection.active = false
    } else if (e.button === 2) {
      this.mouse.rightDown = false
      this.emit('rightClick', {
        type: 'rightClick',
        x: this.mouse.x,
        y: this.mouse.y,
        worldX: this.mouse.worldX,
        worldY: this.mouse.worldY,
        shiftKey: e.shiftKey,
        ctrlKey: e.ctrlKey
      })
      this.emit('rightDragEnd', {
        type: 'rightDragEnd',
        x: this.mouse.x,
        y: this.mouse.y,
        worldX: this.mouse.worldX,
        worldY: this.mouse.worldY,
        shiftKey: e.shiftKey,
        ctrlKey: e.ctrlKey
      })
    } else if (e.button === 1) {
      this.mouse.middleDown = false
    }
  }

  private onMouseMove(e: MouseEvent): void {
    this.mouse.x = e.clientX
    this.mouse.y = e.clientY
    this.updateMouseWorld()

    if (this.drag.active) {
      this.drag.currentX = this.mouse.worldX
      this.drag.currentY = this.mouse.worldY
      this.selection.x2 = this.mouse.worldX
      this.selection.y2 = this.mouse.worldY
    }
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault()

    // Detect pinch-to-zoom vs two-finger pan
    // Pinch-to-zoom: ctrlKey is true on trackpad pinch gestures
    // Two-finger pan: normal scroll with deltaX and deltaY
    if (e.ctrlKey) {
      // Pinch-to-zoom
      this.emit('scroll', {
        type: 'scroll',
        delta: e.deltaY,
        x: this.mouse.x,
        y: this.mouse.y,
        worldX: this.mouse.worldX,
        worldY: this.mouse.worldY
      })
    } else {
      // Two-finger pan - emit pan event with deltaX and deltaY
      this.emit('pan', {
        type: 'pan',
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        x: this.mouse.x,
        y: this.mouse.y,
        worldX: this.mouse.worldX,
        worldY: this.mouse.worldY
      })
    }
  }

  private onKeyDown(e: KeyboardEvent): void {
    // Don't capture if typing in an input
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return
    }

    this.keys.add(e.key.toLowerCase())
    this.emit('keyDown', {
      type: 'keyDown',
      key: e.key.toLowerCase(),
      shiftKey: e.shiftKey,
      ctrlKey: e.ctrlKey
    })
  }

  private onKeyUp(e: KeyboardEvent): void {
    this.keys.delete(e.key.toLowerCase())
    this.emit('keyUp', {
      type: 'keyUp',
      key: e.key.toLowerCase(),
      shiftKey: e.shiftKey,
      ctrlKey: e.ctrlKey
    })
  }

  // Event subscription
  on(type: InputEventType, callback: InputCallback): void {
    if (!this.callbacks.has(type)) {
      this.callbacks.set(type, [])
    }
    this.callbacks.get(type)!.push(callback)
  }

  off(type: InputEventType, callback: InputCallback): void {
    const callbacks = this.callbacks.get(type)
    if (callbacks) {
      const index = callbacks.indexOf(callback)
      if (index !== -1) {
        callbacks.splice(index, 1)
      }
    }
  }

  private emit(type: InputEventType, event: InputEvent): void {
    const callbacks = this.callbacks.get(type)
    if (callbacks) {
      for (const cb of callbacks) {
        cb(event)
      }
    }
  }

  // Check if a key is currently pressed
  isKeyDown(key: string): boolean {
    return this.keys.has(key.toLowerCase())
  }

  // Get selection box in world coordinates (normalized so x1 < x2, y1 < y2)
  getSelectionBox(): SelectionBox {
    return {
      active: this.selection.active,
      x1: Math.min(this.selection.x1, this.selection.x2),
      y1: Math.min(this.selection.y1, this.selection.y2),
      x2: Math.max(this.selection.x1, this.selection.x2),
      y2: Math.max(this.selection.y1, this.selection.y2)
    }
  }

  // Get edge scrolling delta based on mouse position
  getEdgeScrollDelta(dt: number): { dx: number; dy: number } {
    let dx = 0
    let dy = 0

    if (this.mouse.x < this.edgeScrollMargin) {
      dx = -this.edgeScrollSpeed * dt
    } else if (this.mouse.x > this.renderer.width - this.edgeScrollMargin) {
      dx = this.edgeScrollSpeed * dt
    }

    if (this.mouse.y < this.edgeScrollMargin) {
      dy = -this.edgeScrollSpeed * dt
    } else if (this.mouse.y > this.renderer.height - this.edgeScrollMargin) {
      dy = this.edgeScrollSpeed * dt
    }

    return { dx, dy }
  }

  // Keyboard camera movement
  getKeyboardCameraDelta(dt: number): { dx: number; dy: number } {
    let dx = 0
    let dy = 0
    const speed = this.edgeScrollSpeed * dt

    if (this.isKeyDown('a') || this.isKeyDown('arrowleft')) dx -= speed
    if (this.isKeyDown('d') || this.isKeyDown('arrowright')) dx += speed
    if (this.isKeyDown('w') || this.isKeyDown('arrowup')) dy -= speed
    if (this.isKeyDown('s') || this.isKeyDown('arrowdown')) dy += speed

    return { dx, dy }
  }

  destroy(): void {
    this.canvas.removeEventListener('mousedown', this.onMouseDown)
    this.canvas.removeEventListener('mouseup', this.onMouseUp)
    this.canvas.removeEventListener('mousemove', this.onMouseMove)
    this.canvas.removeEventListener('wheel', this.onWheel)
    document.removeEventListener('keydown', this.onKeyDown)
    document.removeEventListener('keyup', this.onKeyUp)
    this.callbacks.clear()
  }
}
